// Lib
import React from 'react';
import PropTypes from 'prop-types';
import * as Immutable from 'immutable';

// Utils
import { getAllCellsBetween } from '../../../../../../element/table/utils/selectionUtils/tableSelectionGeometryUtils';
import { getTableContentCellTextStyle } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { getElementDefaultColors } from '../../editorTools/textStylePopup/textStylePopupUtils';

// Components
import LegacyTextStyleRichTextToolPopup from './LegacyTextStyleRichTextToolPopup';
import createSwitchableTableComponent from '../../tableTools/createSwitchableTableComponent';
import DraftTextColorButton from './DraftTextColorButton';
import TextStylePopupContent from '../../editorTools/textStylePopup/TextStylePopupContent';

// Constants
import {
    TEXT_BACKGROUND_PRESETS,
    TEXT_COLOR_PRESETS,
} from '../../../../../../components/editor/plugins/textColor/textColorConstants';
import {
    useActiveTableCallback,
    useActiveTableSelector,
} from '../../../../../../element/table/store/hotTableStoreHooks';
import { selectActiveTableCellSelection } from '../../../../../../element/table/store/selectors/activeHotTableSelectors';
import { updateHotTableSelectedCellTextStyle } from '../../../../../../element/table/utils/hotInstanceUtils/cell/hotTableCellUpdateUtils';

const getCurrentStyle = (currentCellSelections, element) => {
    const selectedCellsCoords = getAllCellsBetween(currentCellSelections);
    const firstSelectedCellCoords = selectedCellsCoords.shift();

    if (!firstSelectedCellCoords) return Immutable.OrderedSet();

    const firstSelectedCellTextStyles = Immutable.OrderedSet(
        getTableContentCellTextStyle(firstSelectedCellCoords)(element),
    );

    return selectedCellsCoords.reduce(
        (acc, cellCoords) => acc.intersect(getTableContentCellTextStyle(cellCoords)(element)),
        firstSelectedCellTextStyles,
    );
};

const TextStyleRichTextToolPopup = ({ showBlockStyleTools, ...rest }) => {
    const { element } = rest;
    const { defaultTextColor, defaultBackgroundColor } = getElementDefaultColors(element) || {};

    const currentCellSelections = useActiveTableSelector(selectActiveTableCellSelection);
    const currentStyle = getCurrentStyle(currentCellSelections, element);

    const onTextColorClick = useActiveTableCallback(
        (activeTable, textStyle) => updateHotTableSelectedCellTextStyle(activeTable.instance, textStyle),
        [],
    );

    return (
        <TextStylePopupContent
            textColorComponents={[
                <DraftTextColorButton
                    key="default"
                    {...rest}
                    preset={null}
                    onTextColorClick={onTextColorClick}
                    currentStyle={currentStyle}
                    customTextColor={defaultTextColor}
                    customBackgroundColor={defaultBackgroundColor}
                />,
                ...Object.values(TEXT_COLOR_PRESETS).map((preset) => (
                    <DraftTextColorButton
                        key={preset}
                        {...rest}
                        preset={preset}
                        onTextColorClick={onTextColorClick}
                        currentStyle={currentStyle}
                        customBackgroundColor={defaultBackgroundColor}
                    />
                )),
            ]}
            textHighlightComponents={Object.values(TEXT_BACKGROUND_PRESETS).map((preset) => (
                <DraftTextColorButton
                    key={preset}
                    {...rest}
                    preset={preset}
                    onTextColorClick={onTextColorClick}
                    currentStyle={currentStyle}
                />
            ))}
        />
    );
};

/***************************************************************
 * Choose which enhancer to use depending on current editor key
 */

const SwitchableTextStyleRichTextToolPopupContent = createSwitchableTableComponent(
    LegacyTextStyleRichTextToolPopup,
    TextStyleRichTextToolPopup,
);

const TextStyleRichTextToolPopupContent = (props) => {
    const { item } = props;
    const showBlockStyleTools = item?.toolSettings?.showBlockStyleTools || props.showBlockStyleTools;

    // TODO-REMOVE-DRAFT - Tables are the only place where we use the DraftJS editor now
    return <SwitchableTextStyleRichTextToolPopupContent {...props} showBlockStyleTools={showBlockStyleTools} />;
};

TextStyleRichTextToolPopupContent.propTypes = {
    showBlockStyleTools: PropTypes.bool,
    element: PropTypes.object,
    editorKey: PropTypes.string,
    item: PropTypes.object,
};

export default TextStyleRichTextToolPopupContent;
